import styled from 'styled-components';

/* ========== HERO ========== */
export const HeroSection = styled.section<{ $backgroundImage: string }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 7rem 2rem 4rem;
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0) 22%
    ),
    linear-gradient(
      135deg,
      rgba(92, 63, 48, 0.68) 0%,
      rgba(141, 107, 88, 0.68) 100%
    ),
    url("${({ $backgroundImage }) => $backgroundImage}"),
    linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.hero} 0%,
      ${({ theme }) => theme.colors.heroDark} 100%
    );
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -20%;
    width: 500px;
    height: 500px;
    background: radial-gradient(
      circle,
      rgba(242, 213, 208, 0.15) 0%,
      transparent 70%
    );
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(
      circle,
      rgba(231, 212, 181, 0.1) 0%,
      transparent 70%
    );
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 6.2rem 1rem 2.75rem;
    background-attachment: scroll;
  }
`;

export const HeroContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  gap: 2rem;
  align-items: center;
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 680px;
`;

export const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  color: ${({ theme }) => theme.colors.secondaryLight};
  padding: 0.4rem 1.2rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.6s ease forwards;
  backdrop-filter: blur(4px);
`;

export const HeroBrand = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 1.2rem;
  line-height: 1.1;
  animation: fadeInUp 0.8s ease forwards;

  span {
    display: block;
    font-style: italic;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.secondary};
    font-size: 0.75em;
    margin-top: 0.3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 3.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: 4.2rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 2rem;
  max-width: 36ch;
  line-height: 1.55;
  animation: fadeInUp 0.8s ease 0.2s forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.98rem;
    line-height: 1.45;
    max-width: 34ch;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease 0.4s forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    gap: 0.75rem;

    > a {
      flex: 1 1 100%;
      width: 100%;
    }
  }
`;

export const HeroImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  animation: scaleIn 0.8s ease 0.3s forwards;
  opacity: 0;
  animation-fill-mode: forwards;
`;

export const HeroGraphic = styled.div<{ $desktopImage: string; $mobileImage: string }>`
  position: relative;
  width: min(100%, 450px);
  aspect-ratio: 1 / 1;
  background-image: url(${({ $desktopImage }) => $desktopImage});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.15));
  animation: float 4s ease-in-out infinite;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
  }

  &::before {
    width: 42px;
    height: 42px;
    top: 14%;
    right: 8%;
    background: rgba(242, 213, 208, 0.5);
  }

  &::after {
    width: 36px;
    height: 36px;
    left: 10%;
    bottom: 12%;
    background: rgba(231, 212, 181, 0.55);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: clamp(240px, 78vw, 360px);
    background-image: url(${({ $mobileImage }) => $mobileImage});

    &::before,
    &::after {
      display: none;
    }
  }

  @media (max-width: 360px) {
    width: clamp(220px, 74vw, 280px);
  }
`;

export const HeroGraphicCaption = styled.p`
  margin-top: 0.45rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
  line-height: 1.45;
`;

/* ========== FEATURES BAR ========== */
export const FeaturesBar = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: 2.4rem 1rem;
  box-shadow: ${({ theme }) => theme.shadows.soft};
  position: relative;
  z-index: 2;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 3rem 2rem;
  }
`;

export const FeaturesContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.secondaryLight};
  box-shadow: 0 10px 14px rgba(107, 79, 62, 0.14);
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    background: transparent;
    box-shadow: none;
    padding: 0;
    border-radius: 0;
  }
`;

export const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.primaryDark};
  flex-shrink: 0;
`;

export const FeatureText = styled.div`
  h4 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primaryDark};
    margin-bottom: 0.2rem;
  }

  p {
    font-size: 0.94rem;
    color: ${({ theme }) => theme.colors.textLight};
    line-height: 1.45;
  }
`;

/* ========== PRODUCTS ========== */
export const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

/* ========== ABOUT ========== */
export const AboutSection = styled.section`
  padding: 3.25rem 1rem;
  background: ${({ theme }) => theme.colors.background};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 6rem 2rem;
  }
`;

export const AboutContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 5rem;
  }
`;

export const AboutImageWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  animation: fadeInLeft 0.8s ease forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    min-height: 350px;
  }
`;

export const AboutContent = styled.div`
  animation: fadeInRight 0.8s ease forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

export const AboutTag = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.secondaryLight};
  color: ${({ theme }) => theme.colors.primaryDark};
  padding: 0.35rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

export const AboutTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 1rem;
  line-height: 1.15;

  span {
    color: ${({ theme }) => theme.colors.accent};
    font-style: italic;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
`;

export const AboutDescription = styled.p`
  font-size: 0.98rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.5;
  margin-bottom: 1.5rem;
  max-width: 52ch;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    line-height: 1.45;
    max-width: 36ch;
  }
`;

export const Divider = styled.div`
  width: 50px;
  height: 3px;
  background: ${({ theme }) => theme.colors.accent};
  margin: 0 auto 1.5rem;
  border-radius: 2px;
`;

/* ========== HOW IT WORKS ========== */
export const HowSection = styled.section`
  padding: 2.75rem 1rem;
  background: ${({ theme }) => theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 5rem 2rem;
  }
`;

export const HowContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

export const HowHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 2.5rem;
  }
`;

export const HowTag = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.secondaryLight};
  color: ${({ theme }) => theme.colors.primaryDark};
  padding: 0.35rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
`;

export const HowTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 0.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.3rem;
  }
`;

export const HowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

export const HowCard = styled.div`
  text-align: center;
  padding: 1.75rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  transition: all ${({ theme }) => theme.transitions.default};
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

export const HowStep = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryDark};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 auto 0.75rem;
`;

export const HowIcon = styled.div`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 0.5rem;
`;

export const HowCardTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 0.3rem;
`;

export const HowCardDesc = styled.p`
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.45;
`;

/* ========== LOCATION ========== */
export const LocationSection = styled.section`
  padding: 2.75rem 1rem;
  background: ${({ theme }) => theme.colors.background};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 5rem 2rem;
  }
`;

export const LocationContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: start;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
  }
`;

export const LocationInfo = styled.div`
  animation: fadeInUp 0.6s ease forwards;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

export const LocationTag = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.secondaryLight};
  color: ${({ theme }) => theme.colors.primaryDark};
  padding: 0.35rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
`;

export const LocationTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 1rem;
  line-height: 1.15;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.3rem;
  }
`;

export const LocationAddress = styled.address`
  font-style: normal;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.65;
  margin-bottom: 1.25rem;

  strong {
    color: ${({ theme }) => theme.colors.primaryDark};
    font-weight: 600;
    display: block;
    margin-bottom: 0.15rem;
  }
`;

export const LocationHint = styled.p`
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

export const LocationActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

export const LocationBadges = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

export const LocationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  font-size: 0.78rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primaryDark};

  svg {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

export const LocationMapWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.card};
  animation: fadeInUp 0.6s ease 0.2s forwards;
  opacity: 0;
  animation-fill-mode: forwards;

  iframe {
    display: block;
    width: 100%;
    height: 280px;
    border: 0;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      height: 420px;
    }
  }
`;

